import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { sendBatchPromotionalCampaign, sendPropertyUpdateEmail } from '../../../services/resendEmailService';
import Icon from '../../../components/AppIcon';

const EmailCampaignPanel = () => {
  const [activeTab, setActiveTab] = useState('campaign');
  const [campaignForm, setCampaignForm] = useState({
    campaign_title: '',
    campaign_body: '',
    campaign_cta_label: 'Découvrir les propriétés',
    campaign_cta_url: 'https://kapitalstates.com',
  });
  const [updateForm, setUpdateForm] = useState({
    user_email: '',
    user_name: '',
    property_title: '',
    property_commune: '',
    update_type: 'price_drop',
    update_description: '',
    property_url: '',
  });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleCampaignChange = (e) => {
    const { name, value } = e?.target;
    setCampaignForm(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdateChange = (e) => {
    const { name, value } = e?.target;
    setUpdateForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSendCampaign = async (e) => {
    e?.preventDefault();
    if (!campaignForm?.campaign_title?.trim()) {
      setError('Le titre de la campagne est requis');
      return;
    }
    setError('');
    setSending(true);
    setResult(null);
    try {
      // Fetch all users who have promotions enabled (or no settings = default allow)
      const { data: users } = await supabase
        ?.from('user_profiles')
        ?.select('id, email, full_name')
        ?.not('email', 'is', null)
        ?.limit(500);

      if (!users?.length) {
        setResult({ type: 'info', message: 'Aucun utilisateur trouvé.' });
        return;
      }

      const recipients = users?.map(u => ({
        user_id: u?.id,
        user_email: u?.email,
        user_name: u?.full_name || u?.email,
      }));

      const stats = await sendBatchPromotionalCampaign(recipients, campaignForm);
      setResult({
        type: 'success',
        message: `Campagne envoyée — ${stats?.sent} envoyés, ${stats?.skipped} ignorés (préférences), ${stats?.failed} échecs.`,
      });
    } catch (err) {
      setError('Erreur lors de l\'envoi de la campagne : ' + err?.message);
    } finally {
      setSending(false);
    }
  };

  const handleSendPropertyUpdate = async (e) => {
    e?.preventDefault();
    if (!updateForm?.user_email?.trim() || !updateForm?.property_title?.trim()) {
      setError('Email destinataire et titre de propriété requis');
      return;
    }
    setError('');
    setSending(true);
    setResult(null);
    try {
      // Try to find user_id from email
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.eq('email', updateForm?.user_email)
        ?.maybeSingle();

      const res = await sendPropertyUpdateEmail({
        user_id: profile?.id || null,
        user_name: updateForm?.user_name || updateForm?.user_email,
        user_email: updateForm?.user_email,
        property_title: updateForm?.property_title,
        property_commune: updateForm?.property_commune,
        update_type: updateForm?.update_type,
        update_description: updateForm?.update_description,
        property_url: updateForm?.property_url,
      });

      if (res?.skipped) {
        setResult({ type: 'info', message: 'Email ignoré — l\'utilisateur a désactivé les emails de mise à jour.' });
      } else if (res?.success) {
        setResult({ type: 'success', message: 'Email de mise à jour envoyé avec succès.' });
      } else {
        setError('Échec de l\'envoi : ' + (res?.error || 'Erreur inconnue'));
      }
    } catch (err) {
      setError('Erreur : ' + err?.message);
    } finally {
      setSending(false);
    }
  };

  const inputClass = "w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1.5";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-1">
          <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
            <Icon name="Mail" size={20} className="text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Campagnes Email</h2>
            <p className="text-sm text-gray-500">Envoyez des campagnes promotionnelles et des alertes de mise à jour de propriétés via Resend</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-gray-100 rounded-xl p-1 w-fit">
        {[
          { id: 'campaign', label: 'Campagne Promotionnelle', icon: 'Megaphone' },
          { id: 'update', label: 'Mise à Jour Propriété', icon: 'Home' },
        ]?.map(tab => (
          <button
            key={tab?.id}
            onClick={() => { setActiveTab(tab?.id); setResult(null); setError(''); }}
            className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab?.id
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Icon name={tab?.icon} size={15} />
            <span>{tab?.label}</span>
          </button>
        ))}
      </div>

      {/* Result / Error */}
      {result && (
        <div className={`flex items-start space-x-3 p-4 rounded-xl border ${
          result?.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-blue-50 border-blue-200 text-blue-800'
        }`}>
          <Icon name={result?.type === 'success' ? 'CheckCircle' : 'Info'} size={18} className="flex-shrink-0 mt-0.5" />
          <p className="text-sm">{result?.message}</p>
        </div>
      )}
      {error && (
        <div className="flex items-start space-x-3 p-4 rounded-xl border bg-red-50 border-red-200 text-red-800">
          <Icon name="AlertCircle" size={18} className="flex-shrink-0 mt-0.5" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Campaign Form */}
      {activeTab === 'campaign' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-5">Nouvelle Campagne Promotionnelle</h3>
          <p className="text-sm text-gray-500 mb-6 bg-amber-50 border border-amber-100 rounded-lg p-3">
            <Icon name="Info" size={14} className="inline mr-1.5 text-amber-600" />
            La campagne sera envoyée à tous les utilisateurs dont les préférences de communication autorisent les emails promotionnels.
          </p>
          <form onSubmit={handleSendCampaign} className="space-y-5">
            <div>
              <label className={labelClass}>Titre de la campagne *</label>
              <input
                type="text"
                name="campaign_title"
                value={campaignForm?.campaign_title}
                onChange={handleCampaignChange}
                placeholder="Ex: 🏡 Nouvelles opportunités immobilières à Abidjan"
                className={inputClass}
                required
              />
            </div>
            <div>
              <label className={labelClass}>Corps du message</label>
              <textarea
                name="campaign_body"
                value={campaignForm?.campaign_body}
                onChange={handleCampaignChange}
                placeholder="Décrivez votre campagne promotionnelle..."
                rows={4}
                className={inputClass + " resize-none"}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Texte du bouton CTA</label>
                <input
                  type="text"
                  name="campaign_cta_label"
                  value={campaignForm?.campaign_cta_label}
                  onChange={handleCampaignChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>URL du bouton CTA</label>
                <input
                  type="url"
                  name="campaign_cta_url"
                  value={campaignForm?.campaign_cta_url}
                  onChange={handleCampaignChange}
                  className={inputClass}
                />
              </div>
            </div>
            <div className="pt-2">
              <button
                type="submit"
                disabled={sending}
                className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-60 transition-colors"
              >
                {sending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Envoi en cours…</span>
                  </>
                ) : (
                  <>
                    <Icon name="Send" size={16} color="white" />
                    <span>Envoyer la campagne</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Property Update Form */}
      {activeTab === 'update' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-5">Envoyer une Alerte de Mise à Jour</h3>
          <p className="text-sm text-gray-500 mb-6 bg-amber-50 border border-amber-100 rounded-lg p-3">
            <Icon name="Info" size={14} className="inline mr-1.5 text-amber-600" />
            Notifiez un utilisateur d'une mise à jour sur une propriété (baisse de prix, nouvelle annonce, etc.).
          </p>
          <form onSubmit={handleSendPropertyUpdate} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Email destinataire *</label>
                <input
                  type="email"
                  name="user_email"
                  value={updateForm?.user_email}
                  onChange={handleUpdateChange}
                  placeholder="client@exemple.com"
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Nom du destinataire</label>
                <input
                  type="text"
                  name="user_name"
                  value={updateForm?.user_name}
                  onChange={handleUpdateChange}
                  placeholder="Prénom Nom"
                  className={inputClass}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Titre de la propriété *</label>
                <input
                  type="text"
                  name="property_title"
                  value={updateForm?.property_title}
                  onChange={handleUpdateChange}
                  placeholder="Villa moderne à Cocody"
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Commune</label>
                <input
                  type="text"
                  name="property_commune"
                  value={updateForm?.property_commune}
                  onChange={handleUpdateChange}
                  placeholder="Cocody"
                  className={inputClass}
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>Type de mise à jour</label>
              <select
                name="update_type"
                value={updateForm?.update_type}
                onChange={handleUpdateChange}
                className={inputClass}
              >
                <option value="price_drop">📉 Baisse de prix</option>
                <option value="new_listing">🆕 Nouvelle annonce</option>
                <option value="status_change">🔔 Changement de statut</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Description de la mise à jour</label>
              <textarea
                name="update_description"
                value={updateForm?.update_description}
                onChange={handleUpdateChange}
                placeholder="Ex: Le prix a été réduit de 120 000 000 FCFA à 95 000 000 FCFA"
                rows={3}
                className={inputClass + " resize-none"}
              />
            </div>
            <div>
              <label className={labelClass}>URL de la propriété</label>
              <input
                type="url"
                name="property_url"
                value={updateForm?.property_url}
                onChange={handleUpdateChange}
                placeholder="https://kapitalstates.com/individual-property-page?id=..."
                className={inputClass}
              />
            </div>
            <div className="pt-2">
              <button
                type="submit"
                disabled={sending}
                className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-60 transition-colors"
              >
                {sending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Envoi en cours…</span>
                  </>
                ) : (
                  <>
                    <Icon name="Send" size={16} color="white" />
                    <span>Envoyer l'alerte</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default EmailCampaignPanel;
